<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { AssetVisitType, type IWarehouse } from '@nova/core';

import { CheckinFlowStep, useCheckinFlow } from '@/composables/useCheckinFlow';

defineOptions({ name: 'PurposeOfStopPage' });

// Declared but unused: the step host passes `warehouse` to every step component, and without the
// declaration it would fall through onto the root element as an attribute.
defineProps<{
  warehouse: Partial<IWarehouse>;
}>();

interface PurposeOption {
  key: string;
  value: AssetVisitType;
  label: string;
  icons: string[];
}

interface PurposeGroup {
  label: string;
  options: PurposeOption[];
}

const { t } = useI18n();
const checkinFlow = useCheckinFlow();

const selectedKey = ref<string | null>(null);

const groups = computed<PurposeGroup[]>(() => [
  {
    label: t('appointmentForm.visitGroupInbound'),
    options: [
      {
        key: 'unload',
        value: AssetVisitType.LiveUnload,
        label: t('appointmentForm.visitUnloadTruck'),
        icons: ['mdi-arrow-left-thin', 'mdi-truck-outline']
      },
      {
        key: 'drop',
        value: AssetVisitType.Drop,
        label: t('appointmentForm.visitDropTrailer'),
        icons: ['mdi-hook-off']
      }
    ]
  },
  {
    label: t('appointmentForm.visitGroupOutbound'),
    options: [
      {
        key: 'load',
        value: AssetVisitType.LiveLoad,
        label: t('appointmentForm.visitLoadTruck'),
        icons: ['mdi-arrow-right-thin', 'mdi-truck-outline']
      },
      {
        key: 'pickup',
        value: AssetVisitType.PickUp,
        label: t('appointmentForm.visitHookTrailer'),
        icons: ['mdi-hook']
      }
    ]
  },
  {
    label: t('appointmentForm.visitGroupBoth'),
    options: [
      {
        key: 'unload-load',
        value: AssetVisitType.Live,
        label: t('appointmentForm.visitUnloadLoad'),
        icons: ['mdi-swap-horizontal', 'mdi-truck-outline']
      },
      {
        key: 'drop-hook',
        value: AssetVisitType.DropHook,
        label: t('appointmentForm.visitDropHook'),
        icons: ['mdi-hook-off', 'mdi-hook']
      }
    ]
  }
]);

function selectOption(option: PurposeOption) {
  selectedKey.value = option.key;
  checkinFlow.setAssetVisitType(option.value);
  checkinFlow.setLoadTypeId(null);

  checkinFlow.setNextStep(CheckinFlowStep.CheckinDetailsPage);
}

function handleHaveReferenceNumber() {
  checkinFlow.setPreviousStep(CheckinFlowStep.Welcome);
}
</script>

<template>
  <div class="flex-grow-1 d-flex flex-column justify-space-between gap-6">
    <div class="comet-i18n-exclude d-flex flex-column gap-6">
      <p class="typography-heading-sm-bold">{{ t('appointmentForm.purposeTitle') }}</p>

      <div v-for="group in groups" :key="group.label" class="d-flex flex-column gap-3">
        <p class="typography-body-md-bold">{{ group.label }}</p>

        <div class="d-flex flex-column gap-2">
          <v-card
            v-for="option in group.options"
            :key="option.key"
            variant="outlined"
            role="button"
            tabindex="0"
            class="purpose-option-card border--color-neutral-40 pa-4 d-flex align-center gap-3"
            :class="{ 'purpose-option-card--selected': selectedKey === option.key }"
            :data-testid="`comet-purpose-option-${option.key}`"
            :aria-pressed="selectedKey === option.key"
            @click="selectOption(option)"
            @keydown.enter="selectOption(option)"
            @keydown.space.prevent="selectOption(option)">
            <div class="d-flex align-center gap-1">
              <v-icon v-for="icon in option.icons" :key="icon" :icon="icon"></v-icon>
            </div>
            <span class="flex-grow-1 typography-body-sm-bold">{{ option.label }}</span>
            <v-icon size="16">mdi-arrow-right</v-icon>
          </v-card>
        </div>
      </div>
    </div>

    <div class="purpose-footer mx-n6 px-6 pt-4 pb-6 bg--color-neutral-0">
      <v-btn
        block
        height="44"
        variant="outlined"
        prepend-icon="mdi-arrow-left"
        class="comet-i18n-exclude border--color-neutral-40"
        data-testid="comet-purpose-have-reference-number"
        @click="handleHaveReferenceNumber">
        {{ t('initial.haveAppointment') }}
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
.purpose-footer {
  position: sticky;
  bottom: 0;
  z-index: 1;
}

.purpose-option-card {
  border-radius: 8px;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $color-text-link;
    outline-offset: 2px;
  }
}

.purpose-option-card--selected {
  border-color: $color-text-link !important;
  border-width: 2px;
}
</style>
